import debounce from 'lodash.debounce'

/**
 * Amenity accordion beside the connectivity map, and the pins it keys.
 *
 * One row open at a time, because the map next to it can only carry one
 * category's numbering: opening a row closes the rest and swaps the pin group
 * over the drawing. The pins are staggered in and pulled out together — that
 * motion lives in css/base/location.css, which also disables it under
 * `prefers-reduced-motion`; setting a height here stays instant either way.
 *
 * Heights are measured into pixels the way the plans page's legend and the
 * header menu do — `height: auto` can't be transitioned, and there's no reason
 * for the site's third accordion to collapse a different way.
 */
export function initLocationPins() {
    const root = document.querySelector('[data-poi]')
    if (!root) return

    const rows = Array.from(root.querySelectorAll('[data-poi-toggle]')).map((toggle) => ({
        toggle,
        panel: document.getElementById(toggle.getAttribute('aria-controls')),
        pins: root.querySelector(`[data-poi-pins="${toggle.dataset.poiTarget}"]`),
    }))
    if (!rows.length || rows.some(({ panel, pins }) => !panel || !pins)) return

    const setRow = ({ toggle, panel, pins }, open) => {
        toggle.setAttribute('aria-expanded', String(open))
        panel.classList.toggle('is-open', open)
        // Measured on the content, not the panel: once the panel carries a pixel
        // height its own scrollHeight reports that height, not the content's.
        panel.style.height = open ? `${panel.firstElementChild.offsetHeight}px` : '0px'
        pins.setAttribute('aria-hidden', String(!open))
    }

    const openRow = (index) => rows.forEach((row, i) => setRow(row, i === index))

    // The markup ships one row open at `height: auto` so the page reads correctly
    // with no JS. Convert that to the pixel height everything animates against
    // before anything can move — from `auto` the first collapse wouldn't animate.
    const openIndex = rows.findIndex(({ toggle }) => toggle.getAttribute('aria-expanded') === 'true')
    openRow(openIndex)

    rows.forEach(({ toggle }, index) => {
        toggle.addEventListener('click', () => {
            // Pressing the open row again does nothing rather than collapsing it:
            // an all-closed state would strip every pin off the map and leave the
            // drawing with nothing keyed to it.
            if (toggle.getAttribute('aria-expanded') === 'true') return
            openRow(index)
        })
    })

    // The open height is a fixed pixel value, so re-measure when the panel
    // reflows — the longer place names wrap at some widths and not others.
    window.addEventListener(
        'resize',
        debounce(() => {
            const open = rows.find(({ toggle }) => toggle.getAttribute('aria-expanded') === 'true')
            if (open) setRow(open, true)
        }, 100)
    )
}
